import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../shadcn/ui/table";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const ViewAllButton = ({ to, children }) => (
  <Link
    to={to}
    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
  >
    {children}
  </Link>
);

const ShimmerItem = () => (
  <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-100">
    <div className="flex items-center mb-3">
      <div className="w-3 h-3 rounded-full mr-2 bg-gray-200 animate-pulse"></div>
      <div className="h-5 w-1/2 bg-gray-200 rounded animate-pulse"></div>
    </div>

    {/* Image placeholder for property */}
    <div className="h-24 w-full bg-gray-200 rounded mb-4 animate-pulse"></div>

    <div className="space-y-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="border-t border-gray-100 pt-3">
          <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
        </div>
      ))}
    </div>
  </div>
);

const TableShimmer = ({ columnCount = 4, rowCount = 3 }) => (
  <div className="max-h-96 overflow-y-auto">
    {/* Mobile shimmer */}
    <div className="sm:hidden">
      {Array.from({ length: rowCount }).map((_, index) => (
        <ShimmerItem key={index} />
      ))}
    </div>

    {/* Desktop shimmer */}
    <div className="hidden sm:block">
      <Table>
        <TableHeader>
          <TableRow>
            {Array.from({ length: columnCount }).map((_, index) => (
              <TableHead key={index} className="text-left py-3 px-3">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, rowIndex) => (
            <TableRow key={rowIndex} className="border-b border-gray-100">
              {Array.from({ length: columnCount }).map((_, colIndex) => (
                <TableCell key={colIndex} className="py-3 px-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

/**
 * ResponsiveTable - A responsive table component with mobile card view
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Array of column definitions with { key, header, render, mobileLabel, mobileRender }
 * @param {string} props.title - Table title
 * @param {string} props.emptyMessage - Message to display when no data
 * @param {boolean} props.loading - Whether data is loading
 * @param {Object} props.viewAllLink - Object with { to, label } for View All button
 * @param {Function} props.statusIndicator - Function to render status indicator (receives row data)
 * @param {Function} props.onRowClick - Function called when row is clicked (receives row data)
 * @returns {JSX.Element}
 */
const ResponsiveTable = ({
  data = [],
  columns = [],
  title = "",
  emptyMessage = "No data found",
  loading = false,
  viewAllLink,
  statusIndicator,
  onRowClick,
}) => {
  return (
    <motion.div variants={cardVariants} className="bg-white rounded-lg shadow-sm p-3 sm:p-5 h-full  border-gray-100">
      <div className="flex justify-between items-center mb-4">
        {title && <h5 className="font-medium text-base sm:text-lg mb-0">{title}</h5>}
        {viewAllLink && <ViewAllButton to={viewAllLink.to}>{viewAllLink.label}</ViewAllButton>}
      </div>

      {loading ? (
        <TableShimmer columnCount={columns.length} rowCount={3} />
      ) : (
        <>
          {/* Mobile card view */}
          <div className="sm:hidden">
            {data.length > 0 ? (
              data.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-200"
                  onClick={() => onRowClick && onRowClick(item)}
                >
                  <div className="flex items-center mb-3">
                    {statusIndicator && <div className="mr-2">{statusIndicator(item)}</div>}
                    <span className="font-medium text-base truncate max-w-[240px]">
                      {columns[0].mobileRender
                        ? columns[0].mobileRender(item, index)
                        : item[columns[0].key] || "Unnamed"}
                    </span>
                  </div>

                  {/* Property image or first column special case */}
                  {columns[0].key === "property" && columns[0].render && (
                    <div className="mb-3">{columns[0].render(item, index)}</div>
                  )}

                  <div className="space-y-3">
                    {columns.slice(1).map((column, colIndex) => (
                      <div key={colIndex} className="border-t border-gray-100 pt-2">
                        <div className="font-medium text-gray-700 mb-1">{column.mobileLabel || column.header}:</div>
                        <div className="text-sm">
                          {column.mobileRender
                            ? column.mobileRender(item, index)
                            : column.render
                              ? column.render(item, index)
                              : item[column.key] || "N/A"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">{emptyMessage}</div>
            )}
          </div>

          {/* Desktop table view */}
          <div className="hidden sm:block max-h-96 overflow-y-auto overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow className="border-b border-gray-100">
                  {columns.map((column, index) => (
                    <TableHead
                      key={index}
                      className="text-left py-2 px-3 text-xs font-semibold text-gray-600 tracking-wide"
                    >
                      {column.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      className="border-b border-gray-100 hover:bg-gray-50/70"
                      onClick={() => onRowClick && onRowClick(item)}
                      style={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      {columns.map((column, colIndex) => (
                        <TableCell key={colIndex} className="py-2.5 px-3 text-sm align-middle">
                          {colIndex === 0 && statusIndicator ? (
                            <div className="flex items-center gap-2 min-w-0">
                              {statusIndicator(item)}
                              <span className="truncate">
                                {column.render ? column.render(item, rowIndex) : item[column.key] || "N/A"}
                              </span>
                            </div>
                          ) : column.render ? (
                            column.render(item, rowIndex)
                          ) : (
                            item[column.key] || "N/A"
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center py-6 text-gray-500">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default ResponsiveTable;
